import { RiverDefinition, riverCurrent, riverSample } from "game-common/room/river"
import { HillLevels, hillHeightAt } from "game-common/room/hill_height"
import { TileSize } from "game-common/models"

/** Sheltered, rotating wakes downstream of the room’s rock sprites. */
export const riverFeatures = (river: RiverDefinition, hills?: HillLevels) => {
    const slopes = new Map<string, number>()
    const grades = new Map<number, number>()
    const gridGrade = (gx: number, gy: number): number => {
        if (!hills) return 0
        const key = (gx + 32768) * 65536 + gy + 32768
        let value = grades.get(key)
        if (value === undefined) {
            const x = gx * 8, y = gy * 8, s = riverSample(river, x / TileSize, y / TileSize)
            value = Math.max(0, (hillHeightAt(hills, x - s.dx * 4, y - s.dy * 4) -
                hillHeightAt(hills, x + s.dx * 4, y + s.dy * 4)) / 8)
            grades.set(key, value)
        }
        return value
    }
    const gradeAt = (x: number, y: number): number => {
        const gx = Math.floor(x / 8), gy = Math.floor(y / 8), fx = x / 8 - gx, fy = y / 8 - gy
        return (gridGrade(gx, gy) * (1 - fx) + gridGrade(gx + 1, gy) * fx) * (1 - fy) +
            (gridGrade(gx, gy + 1) * (1 - fx) + gridGrade(gx + 1, gy + 1) * fx) * fy
    }
    return (px: number, py: number, depth: number, deeper: number): [number, number, number, number, number, number, number] => {
        const cx = Math.floor(px / TileSize), cy = Math.floor(py / TileSize), key = `${cx},${cy}`
        let slope = slopes.get(key)
        if (slope === undefined) {
            const [dx, dy] = riverCurrent(river, px, py, 30)
            const length = Math.hypot(dx, dy) || 1
            const x = (cx + .5) * TileSize, y = (cy + .5) * TileSize
            slope = hills ? (hillHeightAt(hills, x - dx / length * 8, y - dy / length * 8) -
                hillHeightAt(hills, x + dx / length * 8, y + dy / length * 8)) / 16 : 0
            slopes.set(key, slope)
        }
        const sample = riverSample(river, px / TileSize, py / TileSize)
        let [vx, vy] = riverCurrent(river, px, py, depth, slope, sample)
        const rock = 0 // Stones are world sprites; the water never paints a substitute.
        const length = Math.hypot(vx, vy) || 1, dx = vx / length, dy = vy / length
        for (const stone of river.rocks ?? []) {
            const rx = px - (stone.x + .5) * TileSize, ry = py - (stone.y + .8) * TileSize
            const along = rx * dx + ry * dy, across = -rx * dy + ry * dx
            if (along <= 0 || along >= 25 || Math.abs(across) >= 13) continue
            const wake = Math.sin(Math.PI * along / 25) * Math.exp(-across * across / 150) * .2
            vx -= dx * wake * .7; vy -= dy * wake * .7
            for (const sign of [-1, 1]) {
                const ax = along - 14, ay = across - sign * 8
                const swirl = Math.exp(-ax * ax / 160 - ay * ay / 70) * .22
                const forward = -sign * ay / 7 * swirl, sideways = sign * ax / 10 * swirl
                vx += dx * forward - dy * sideways; vy += dy * forward + dx * sideways
            }
        }
        const pool = .5 + .5 * Math.sin(px / 119 + Math.sin(py / 83) + river.seed)
        const core = Math.max(0, Math.min(1, deeper * (.5 + pool * .8) - Math.max(0, slope) * .35))
        // The foam footprint is terrain data. A second, upstream sample carries it into the plunge pool.
        const drop = hills ? Math.max(gradeAt(px, py) * .75,
            gradeAt(px - sample.dx * 12, py - sample.dy * 12) * .9) : 0
        const fall = Math.max(0, Math.min(1, (drop - .12) / .7)) * (.3 + .7 * Math.min(1, depth / 12))
        return [vx, vy, core, rock, sample.along * TileSize, sample.side * TileSize, fall]
    }
}
